/*
 * mdv-password-eye.js - show / hide toggle for customer password fields.
 * Maniere De Voir, 28 Sep 2026. Contract: staging-20260928-password-eye/CONTRACT-2026-09-28.md
 * as amended by AMENDMENT-W1-2026-09-28.md (incl. W1a).
 *
 * Progressive enhancement. Without this script, or on any error, every field is
 * left exactly as the theme renders it (no wrapper, no button, no padding).
 * Scope: input[type="password"][name^="customer["] - Shopify customer forms only
 * (login, register, wishlist pop-ups, reset, activate). The storefront password
 * page (name="password") and checkout are out of scope.
 *
 * Eye / eye-off glyphs: Feather icons (https://github.com/feathericons/feather),
 * MIT License, (c) Cole Bemis. Stroke colour changed to currentColor.
 * Comments are stripped at the CDN; the version marker is a string literal.
 */
(function () {
  'use strict';

  try {
    if (window.mdvPwEye || typeof WeakSet !== 'function' || !document.querySelectorAll) return;
  } catch (e) {
    return;
  }

  var SELECTOR = 'input[type="password"][name^="customer["]';
  var LABEL_SHOW = 'Show password';
  var LABEL_HIDE = 'Hide password';
  var ATTR = 'data-mdv-pw-eye-input';
  var STYLE_ID = 'mdv-pw-eye-style';
  var TOLERANCE = 0.5;

  var SVG_SHOW = '<svg class="mdv-pw-eye__show" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
  var SVG_HIDE = '<svg class="mdv-pw-eye__hide" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>';

  var CSS = [
    '.mdv-pw-wrap{display:block;position:relative;width:100%}',
    '.mdv-pw-wrap>input[data-mdv-pw-eye-input="1"]{padding-right:36px!important;padding-inline-end:36px!important;text-transform:none!important}',
    '.mdv-pw-wrap--center>input[data-mdv-pw-eye-input="1"]{padding-left:36px!important;padding-inline-start:36px!important}',
    '.mdv-pw-wrap>input[data-mdv-pw-eye-input="1"]::-ms-reveal,.mdv-pw-wrap>input[data-mdv-pw-eye-input="1"]::-ms-clear{display:none}',
    '.mdv-pw-wrap>button.mdv-pw-eye,.mdv-pw-wrap>button.mdv-pw-eye:hover,.mdv-pw-wrap>button.mdv-pw-eye:focus,.mdv-pw-wrap>button.mdv-pw-eye:active{position:absolute;top:0;bottom:0;right:0;width:36px;height:auto;min-height:0;margin:0;padding:0;border:0;border-radius:0;background:transparent;color:var(--brown,#4b3e3c);display:flex;align-items:center;justify-content:center;line-height:0;cursor:pointer;-webkit-appearance:none;appearance:none;box-shadow:none}',
    '@supports (inset-inline-end:0){.mdv-pw-wrap>button.mdv-pw-eye,.mdv-pw-wrap>button.mdv-pw-eye:hover,.mdv-pw-wrap>button.mdv-pw-eye:focus,.mdv-pw-wrap>button.mdv-pw-eye:active{right:auto;inset-inline-end:0}}',
    '.mdv-pw-wrap>button.mdv-pw-eye svg{width:16px;height:16px;max-width:none;display:block}',
    '.mdv-pw-wrap>button.mdv-pw-eye[aria-pressed="true"] .mdv-pw-eye__show,.mdv-pw-wrap>button.mdv-pw-eye:not([aria-pressed="true"]) .mdv-pw-eye__hide{display:none}',
    '.mdv-pw-wrap>button.mdv-pw-eye:focus-visible{outline:2px solid #000!important;outline-offset:-3px!important;box-shadow:none!important}',
    '#customer-reset-password .mdv-pw-wrap:not(:last-of-type){margin-bottom:26px}'
  ].join('\n');

  // Every input this script has processed (enhanced, skipped or failed). An input
  // that carries the attribute but is not in here is a copy of enhanced markup
  // (cloneNode / innerHTML): its button has no listeners, so it is rebuilt.
  var seen = new WeakSet();
  var idSeq = 0;
  var observerQueued = false;

  var api = { version: 'mdvPwEye-1.0', count: 0, enhance: enhanceAll };
  window.mdvPwEye = api;

  function matches(el, selector) {
    var fn = el.matches || el.msMatchesSelector || el.webkitMatchesSelector;
    return !!fn && fn.call(el, selector);
  }

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.appendChild(document.createTextNode(CSS));
    (document.head || document.documentElement).appendChild(style);
  }

  function sameRect(a, b) {
    return Math.abs(a.top - b.top) <= TOLERANCE && Math.abs(a.left - b.left) <= TOLERANCE
      && Math.abs(a.width - b.width) <= TOLERANCE && Math.abs(a.height - b.height) <= TOLERANCE;
  }

  function eyeFor(input) {
    var wrap = input.parentNode;
    if (!wrap || wrap.nodeType !== 1 || !wrap.hasAttribute('data-mdv-pw-eye-wrap')) return null;
    return wrap.querySelector('button[data-mdv-pw-eye]');
  }

  function setState(input, button, revealed) {
    input.type = revealed ? 'text' : 'password';
    button.setAttribute('aria-pressed', revealed ? 'true' : 'false');
    button.setAttribute('aria-label', revealed ? LABEL_HIDE : LABEL_SHOW);
    if (input.id) button.setAttribute('aria-controls', input.id);
  }

  // Put the input back where the wrapper sits and drop the wrapper (and its button).
  function unwrap(input, wrap) {
    var parent = wrap && wrap.parentNode;
    if (!parent) return;
    if (input.parentNode === wrap) parent.insertBefore(input, wrap);
    parent.removeChild(wrap);
  }

  // Copy of enhanced markup: restore the plain field so it can be enhanced afresh.
  function scrubCopy(input) {
    var wrap = input.parentNode;
    if (wrap && wrap.nodeType === 1 && wrap.hasAttribute('data-mdv-pw-eye-wrap')) unwrap(input, wrap);
    if (input.type === 'text') input.type = 'password';
    input.removeAttribute(ATTR);
  }

  // Batch layout guard (Amendment W1), used by every enhanceAll pass including
  // MutationObserver passes: measure every rendered field BEFORE any is wrapped,
  // build all, then compare - so a sibling's wrap cannot shift a field between
  // its own before/after reads (reset_password :last-of-type margins).
  function prepare(input) {
    if (seen.has(input)) return null;
    if (input.hasAttribute(ATTR)) scrubCopy(input);
    if (!matches(input, SELECTOR) || !input.parentNode) return null;
    seen.add(input);
    var rendered = input.offsetParent !== null;
    return { input: input, originalId: input.id, rendered: rendered, hadFocus: document.activeElement === input,
      before: rendered ? input.getBoundingClientRect() : null, wrap: null, button: null };
  }

  function build(r) {
    var input = r.input;
    var wrap = document.createElement('span');
    r.wrap = wrap;
    wrap.className = 'mdv-pw-wrap';
    if (window.getComputedStyle(input).textAlign === 'center') wrap.className += ' mdv-pw-wrap--center';
    wrap.setAttribute('data-mdv-pw-eye-wrap', '');
    input.parentNode.insertBefore(wrap, input);
    wrap.appendChild(input);
    if (!input.id || document.getElementById(input.id) !== input) {
      var candidate;
      do { candidate = 'mdv-pw-field-' + (++idSeq); } while (document.getElementById(candidate));
      input.id = candidate;
    }
    var button = document.createElement('button');
    r.button = button;
    button.type = 'button';
    button.className = 'mdv-pw-eye';
    button.setAttribute('data-mdv-pw-eye', '1');
    button.setAttribute('aria-label', LABEL_SHOW);
    button.setAttribute('aria-pressed', 'false');
    button.setAttribute('aria-controls', input.id);
    button.innerHTML = SVG_SHOW + SVG_HIDE;
    wrap.appendChild(button);
    input.setAttribute(ATTR, '1');
  }

  function revert(r, attrValue) {
    var input = r.input;
    if (r.wrap) unwrap(input, r.wrap);
    if (input.id !== r.originalId) {
      if (r.originalId) input.id = r.originalId;
      else input.removeAttribute('id');
    }
    if (attrValue) input.setAttribute(ATTR, attrValue);
    else input.removeAttribute(ATTR);
    if (input.type === 'text') input.type = 'password';
  }

  function bindButton(input, button) {
    var pointerKeepFocus = false;

    button.addEventListener('mousedown', function (event) {
      try {
        pointerKeepFocus = document.activeElement === input;
        if (pointerKeepFocus) event.preventDefault();
      } catch (e) { pointerKeepFocus = false; }
    });

    // A keyboard activation must never inherit a stale pointer flag (e.g. a
    // mousedown that was dragged off the button and never became a click).
    button.addEventListener('keydown', function () {
      pointerKeepFocus = false;
    });

    button.addEventListener('click', function () {
      try {
        var wasFocused = pointerKeepFocus || document.activeElement === input;
        pointerKeepFocus = false;
        var start = null;
        var end = null;
        var direction = null;
        try {
          start = input.selectionStart;
          end = input.selectionEnd;
          direction = input.selectionDirection;
        } catch (e) { start = null; }

        setState(input, button, input.type === 'password');

        if (wasFocused) {
          try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
          if (start !== null && end !== null) {
            try { input.setSelectionRange(start, end, direction || 'none'); } catch (e) { /* type without selection API */ }
          }
        }
      } catch (err) {
        pointerKeepFocus = false;
      }
    });
  }

  // Back to type=password before anything submits (native submit event, or a
  // submit-button click that a script turns into a programmatic form.submit()).
  function rehideWithin(root) {
    if (!root || !root.querySelectorAll) return;
    var fields = root.querySelectorAll('input[data-mdv-pw-eye-input="1"]');
    for (var i = 0; i < fields.length; i++) {
      var field = fields[i];
      if (field.type !== 'text') continue;
      var button = eyeFor(field);
      if (button) setState(field, button, false);
      else field.type = 'password';
    }
  }

  function enhanceAll() {
    try {
      var inputs = document.querySelectorAll(SELECTOR + ', input[' + ATTR + ']');
      var recs = [];
      var i;
      for (i = 0; i < inputs.length; i++) {
        try { var r = prepare(inputs[i]); if (r) recs.push(r); } catch (e) { /* next field */ }
      }
      if (!recs.length) return api.count;
      injectStyle();
      for (i = 0; i < recs.length; i++) {
        try { build(recs[i]); } catch (e) { recs[i].failed = true; try { revert(recs[i], null); } catch (e2) { /* fail open */ } }
      }
      for (i = 0; i < recs.length; i++) {
        var rec = recs[i];
        if (rec.failed) continue;
        try {
          if (rec.rendered && !sameRect(rec.before, rec.input.getBoundingClientRect())) { revert(rec, 'skipped'); continue; }
          if (rec.hadFocus && document.activeElement !== rec.input) {
            try { rec.input.focus({ preventScroll: true }); } catch (e) { rec.input.focus(); }
          }
          bindButton(rec.input, rec.button);
          api.count++;
        } catch (e) { try { revert(rec, null); } catch (e2) { /* fail open */ } }
      }
    } catch (err) { /* fail open */ }
    return api.count;
  }

  function queueEnhance() {
    if (observerQueued) return;
    observerQueued = true;
    var raf = window.requestAnimationFrame || function (cb) { return window.setTimeout(cb, 16); };
    raf(function () {
      observerQueued = false;
      enhanceAll();
    });
  }

  function start() {
    try {
      document.addEventListener('submit', function (event) {
        try { rehideWithin(event.target); } catch (e) { /* fail open */ }
      }, true);

      document.addEventListener('click', function (event) {
        try {
          var target = event.target;
          if (!target || !target.closest) return;
          var trigger = target.closest('input[type="submit"], button[type="submit"], button:not([type])');
          if (trigger && trigger.form) rehideWithin(trigger.form);
        } catch (e) { /* fail open */ }
      }, true);

      enhanceAll();

      if (window.MutationObserver && document.body) {
        new MutationObserver(queueEnhance).observe(document.body, { childList: true, subtree: true });
      }
    } catch (err) { /* fail open */ }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
}());
